import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, createStyles, Theme } from '@bb-ui/react-library';
import { orderBy, get, debounce, uniqBy } from 'lodash';
import { PageTemplate } from 'components/PageTemplate';
import { apiUrl } from 'utils/apiUrl';
import { LearnInstance } from '@bb-learn-instances-service/common';
import useRestApi from 'hooks/useRestApi';
import { ErrorMessage } from 'components/ErrorMessage';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { LearnSitesTable } from './LearnSitesTable';
import { TextField, MenuItem } from '@bb-ui/react-library/dist/components';
import { PrimaryButton } from '@bb-ui/react-library/dist/components/Button';
import { Download } from '@bb-ui/icons/dist/small';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { useReaperLogDownload } from './hooks/useReaperLogDownload';
import { SITE_TYPE_FILTERS, SiteTypeFilter, matchesSiteTypeFilter } from 'utils/siteType';

const POWER_BI_REPORT_URL =
  'https://app.powerbi.com/links/HjBJT5C_C6?ctid=75853e87-aaca-4625-a00a-5a7ca2fe1a72&pbi_source=linkShare&bookmarkGuid=57f89617-afde-4579-8c7e-0af24fc5d9b4';
const POWER_BI_REPORT_URL_GOV =
  'https://app.powerbi.com/links/lo_GjNAFsk?ctid=75853e87-aaca-4625-a00a-5a7ca2fe1a72&pbi_source=linkShare';

const CLEAN_UP_LOG_STAGES = ['local', 'dev', 'int', 'tp'];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    filtersContainer: {
      display: 'flex',
      marginBottom: theme.spacing(4),
      gap: theme.spacing(2),
      alignItems: 'flex-start',
    },
    regionSelect: {
      backgroundColor: theme.palette.background.paper,
      minWidth: 150,
    },
    searchField: {
      flexGrow: 1,
    },
    helperText: {
      color: theme.palette.text.secondary,
      transition: 'opacity 0.2s ease',
    },
  }),
);

export const LearnSites: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const env = useAppConfigContext();
  const regions = env.houstonSupportedRegions ?? ['us-east-1'];
  const isProd = env.stage === 'prod';
  const showCleanUpLog = CLEAN_UP_LOG_STAGES.includes(env.stage ?? '');
  const isGov = regions.includes('us-gov-west-1');
  const reportUrl = isGov ? POWER_BI_REPORT_URL_GOV : POWER_BI_REPORT_URL;

  const { downloadCleanUpLog, isDownloading: isDownloadingCleanUpLog } = useReaperLogDownload();

  const [selectedRegion, setSelectedRegion] = React.useState<string>(regions[0]);
  const [selectedStatus, setSelectedStatus] = React.useState<string>('Installed');
  const [selectedSiteType, setSelectedSiteType] = React.useState<SiteTypeFilter>('all');
  const [sites, setSites] = React.useState<LearnInstance[]>([]);
  const [nextPageToken, setNextPageToken] = React.useState<string | undefined>(undefined);
  const [inputValue, setInputValue] = React.useState<string>('');
  const [searchFilter, setSearchFilter] = React.useState<string>('');
  const [isSearching, setIsSearching] = React.useState<boolean>(false);
  const [searchExhausted, setSearchExhausted] = React.useState<boolean>(false);
  const [allDataFetched, setAllDataFetched] = React.useState<boolean>(false);
  const [isSearchFocused, setIsSearchFocused] = React.useState<boolean>(false);

  const mountedRef = React.useRef(true);
  const currentRegionRef = React.useRef(selectedRegion);
  // Dedupe search pagination: avoid re-fetching the same nextPageToken when the
  // auto-load effect re-renders during the walk.
  const lastRequestedTokenRef = React.useRef<string | undefined>(undefined);
  // Larger page size while walking pages during search (initial load uses the
  // API default of 100). Caps at the backend MAX_PAGE_SIZE.
  const SEARCH_PAGE_SIZE = 1000;

  const {
    data: siteData,
    loading,
    error,
    fetch: fetchLearnSites,
  } = useRestApi(apiUrl('lct', `regions/${selectedRegion}/instances/learnInstances`));

  const debouncedSetSearch = React.useMemo(
    () =>
      debounce((value: string) => {
        if (mountedRef.current) {
          setSearchFilter(value);
          setIsSearching(value.trim() !== '');
          setSearchExhausted(false);
        }
      }, 250),
    [],
  );

  React.useEffect(() => {
    currentRegionRef.current = selectedRegion;
  }, [selectedRegion]);

  const resetState = React.useCallback(() => {
    if (mountedRef.current) {
      setSites([]);
      setNextPageToken(undefined);
      setInputValue('');
      setSearchFilter('');
      setIsSearching(false);
      setSearchExhausted(false);
      setAllDataFetched(false);
      lastRequestedTokenRef.current = undefined;
    }
    debouncedSetSearch.cancel();
  }, [debouncedSetSearch]);

  React.useEffect(
    () => () => {
      mountedRef.current = false;
      debouncedSetSearch.cancel();
    },
    [debouncedSetSearch],
  );

  React.useEffect(() => {
    resetState();

    const timeoutId = setTimeout(() => {
      if (mountedRef.current && currentRegionRef.current === selectedRegion) {
        fetchLearnSites().catch((error) => {
          if (error?.message !== 'Cancel' && mountedRef.current) {
            console.error('Fetch error:', error);
          }
        });
      }
    }, 10);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [selectedRegion, fetchLearnSites, resetState]);

  React.useEffect(() => {
    if (siteData?.items && mountedRef.current) {
      setSites((prev) => {
        const combined = [...prev, ...siteData.items];
        const unique = uniqBy(combined.reverse(), 'TenantId').reverse();
        return unique;
      });

      setNextPageToken(siteData.nextPageToken);

      if (!siteData.nextPageToken) {
        setAllDataFetched(true);
      }
    }
  }, [siteData]);

  const statusOptions = React.useMemo(() => {
    const nonLegacySites = sites.filter((site) => site.FleetId?.toLowerCase() !== 'legacy');
    const statuses = Array.from(new Set(nonLegacySites.map((s) => s.Status))).sort();
    return statuses;
  }, [sites]);

  const handleRegionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRegion(e.target.value);
    if (searchFilter.trim()) {
      setIsSearching(false);
      setSearchExhausted(false);
      setTimeout(() => {
        if (mountedRef.current) {
          setIsSearching(searchFilter.trim() !== '');
          setSearchExhausted(false);
        }
      }, 100);
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedStatus(e.target.value);
  };

  const handleSiteTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSiteType(e.target.value as SiteTypeFilter);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setInputValue(value);
    debouncedSetSearch(value);
  };

  const filteredSites = React.useMemo(() => {
    const query = searchFilter.trim().toLowerCase();

    let filtered = sites.filter((site) => {
      // Exclude legacy sites
      if (site.FleetId?.toLowerCase() === 'legacy') {
        return false;
      }

      if (site.Region !== selectedRegion) {
        return false;
      }

      if (selectedStatus !== 'All' && site.Status !== selectedStatus) {
        return false;
      }

      if (!matchesSiteTypeFilter(site, selectedSiteType)) {
        return false;
      }

      if (query) {
        const privateUrl =
          site.InstallationId && site.FleetId
            ? `${site.InstallationId}.${site.FleetId}.blackboard.com`
            : '';

        const searchableFields = [
          site.InstallationId || '',
          site.Name || '',
          site.Url || '',
          site.ClientName || '',
          privateUrl,
          site.TenantId || '',
          site.SalesforceId || '',
        ].map((field) => field.toLowerCase());

        return searchableFields.some((field) => field.includes(query));
      }

      return true;
    });

    filtered = uniqBy(filtered, 'TenantId');
    return orderBy(filtered, [(site) => get(site, 'InstallationId')?.toLowerCase() ?? ''], ['asc']);
  }, [sites, selectedStatus, selectedSiteType, searchFilter, selectedRegion]);

  // Auto-load more sites if searching
  React.useEffect(() => {
    if (isSearching && !loading && mountedRef.current && nextPageToken && !allDataFetched) {
      const hasNoResults = filteredSites.length === 0;
      const hasFewResults = filteredSites.length > 0 && filteredSites.length < 30;
      const shouldContinueSearch = hasNoResults || hasFewResults;

      if (
        shouldContinueSearch &&
        !searchExhausted &&
        lastRequestedTokenRef.current !== nextPageToken
      ) {
        lastRequestedTokenRef.current = nextPageToken;
        fetchLearnSites({
          params: {
            nextPageToken,
            pageSize: SEARCH_PAGE_SIZE,
          },
        }).catch((error) => {
          if (error?.message !== 'Cancel' && mountedRef.current) {
            console.error('Search pagination error:', error);
          }
        });
      }
    } else if (
      isSearching &&
      (!nextPageToken || allDataFetched) &&
      filteredSites.length === 0 &&
      mountedRef.current
    ) {
      setSearchExhausted(true);
    }
  }, [
    isSearching,
    loading,
    filteredSites.length,
    nextPageToken,
    searchExhausted,
    allDataFetched,
    fetchLearnSites,
  ]);

  const loadMore = () => {
    if (nextPageToken && !allDataFetched && mountedRef.current) {
      fetchLearnSites({
        params: {
          nextPageToken,
          pageSize: SEARCH_PAGE_SIZE,
        },
      }).catch((error) => {
        if (error?.message !== 'Cancel' && mountedRef.current) {
          console.error('Load more error:', error);
        }
      });
    }
  };

  const initialLoad = loading && sites.length === 0;
  const tableLoad = sites.length > 0;

  let content;
  if (error) {
    content = (
      <ErrorMessage
        data-testid="learn-site-list-error"
        title={t('learnSites.loadError')}
        message={error.message}
        variant="block"
      />
    );
  } else if (initialLoad) {
    content = <LoadingIndicator data-testid="site-list-init" />;
  } else {
    content = (
      <LearnSitesTable
        sites={filteredSites}
        nextPageToken={nextPageToken}
        loading={loading && tableLoad}
        loadMore={loadMore}
        isSearching={isSearching}
        searchExhausted={searchExhausted}
        allDataFetched={allDataFetched}
      />
    );
  }

  return (
    <PageTemplate title={t('learnSites.pageTitle')} data-testid="license-list-page">
      <div className={classes.filtersContainer}>
        <TextField
          select
          id="region-select"
          label={t('learnSites.selectRegionLabel')}
          value={selectedRegion}
          onChange={handleRegionChange}
          className={classes.regionSelect}
          floatingLabel
        >
          {regions.map((region) => (
            <MenuItem key={region} value={region}>
              {region}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          id="status-select"
          label={t('learnSites.selectStatusLabel')}
          value={selectedStatus}
          onChange={handleStatusChange}
          className={classes.regionSelect}
          floatingLabel
        >
          <MenuItem value="All">{t('learnSites.all')}</MenuItem>
          {statusOptions.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          id="site-type-select"
          label={t('learnSites.selectSiteTypeLabel')}
          value={selectedSiteType}
          onChange={handleSiteTypeChange}
          className={classes.regionSelect}
          floatingLabel
        >
          {SITE_TYPE_FILTERS.map((option) => (
            <MenuItem key={option} value={option}>
              {t(`learnSites.siteTypes.${option}`)}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          id="search-field"
          label={t('learnSites.searchLabel')}
          value={inputValue}
          onChange={handleSearchChange}
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
          className={classes.searchField}
          floatingLabel
          helperText={
            isSearchFocused && !inputValue.trim() ? t('learnSites.searchHelperText') : ' '
          }
          FormHelperTextProps={{
            'aria-live': 'polite',
            className: classes.helperText,
          }}
        />

        {isProd && (
          <PrimaryButton
            data-testid="instance-report-button"
            onClick={() => window.open(reportUrl, '_blank', 'noopener,noreferrer')}
          >
            Instance Report
          </PrimaryButton>
        )}

        {showCleanUpLog && (
          <PrimaryButton
            data-testid="clean-up-log-button"
            variant="outlined"
            startIcon={<Download />}
            disabled={isDownloadingCleanUpLog}
            onClick={downloadCleanUpLog}
          >
            {isDownloadingCleanUpLog
              ? t('learnSites.cleanUpLogDownloading')
              : t('learnSites.cleanUpLogButton')}
          </PrimaryButton>
        )}
      </div>
      {content}
    </PageTemplate>
  );
};
